import type { VariantProps } from "class-variance-authority";
import Link from "next/link";
import { type ComponentProps, type ReactNode, type Ref } from "react";

import { cn } from "../../utils/cn.util";
import { Checkbox } from "../checkbox";
import { CheckIcon, ChevronRightIcon } from "../icons";
import { RadioGroup } from "../radio-group";
import { MenuItemLeading, type MenuItemLeadingValue } from "./MenuItemLeading";
import { ItemShortcut, type MenuItemShortcut } from "./MenuItemShortcut";
import { menuItemIconVariants, menuItemVariants } from "./styles";

/**
 * What a row reads, one of:
 * - `label`, and an optional `description`: muted text after it, such as a person's email, that gives way first when
 *   the row runs out of room;
 * - `children`, for a row the two can't express.
 */
export type MenuItemContentProps =
  | { label: ReactNode; description?: ReactNode; children?: never }
  | { children?: ReactNode; label?: never; description?: never };

// The content props of a component handing them on to `MenuItem`, typed again as one of the two.
export const menuItemContent = (label: ReactNode, description: ReactNode, children: ReactNode): MenuItemContentProps =>
  label == null ? { children } : { label, description };

/** Where a row goes: it renders as a Next `Link`, so it opens like one (in a new tab, on middle-click, …). */
export type MenuItemLink = Pick<ComponentProps<typeof Link>, "href" | "target" | "prefetch" | "replace" | "scroll">;

export type MenuItemProps = Omit<ComponentProps<"div">, "color" | "children"> &
  VariantProps<typeof menuItemVariants> &
  MenuItemContentProps & {
    // Where the row goes, for a plain row: a checkbox or a radio picks, it doesn't go anywhere. The row renders as the
    // link itself, so a primitive's `asChild` makes one element both the menu item and the anchor.
    link?: MenuItemLink;
    // Before the label: `{ icon }`, `{ avatar }`, `{ countryCode }` or `{ dot }`, sized by the row, or an element for
    // the exceptional row none of them fits.
    leading?: MenuItemLeadingValue;
    // At the row's end, before the shortcut and the check, such as actions shown on hover.
    trailing?: ReactNode;
    // The row opens a submenu, or a nested list: it ends with a chevron.
    hasSubmenu?: boolean;
    shortcut?: MenuItemShortcut;
    // Whether the row is picked. Radix's select, checkbox and radio items mark it themselves (`data-state="checked"`),
    // so it's only needed under a primitive that doesn't, like cmdk.
    // A checkbox row can also be part picked (`"indeterminate"`), such as an "All" row over some of its options.
    checked?: boolean | "indeterminate";
  };

/**
 * The look of a row in any floating list — dropdown menu, select, combobox, command list, filter builder. It has no
 * behaviour of its own: render it inside the primitive that drives the row, through that primitive's `asChild`
 * (`<DropdownMenuPrimitive.Item asChild>`, `<Command.Item asChild>`, …), and it follows that row's highlighted,
 * disabled and checked states.
 */
export const MenuItem = ({
  ref,
  className,
  color,
  variant = "default",
  leading,
  label,
  description,
  trailing,
  hasSubmenu = false,
  shortcut,
  checked: checkedProp,
  link,
  children,
  ...props
}: MenuItemProps) => {
  // Handed down by the Radix primitive through `asChild`.
  const attributes = props as Record<string, unknown>;
  const state = checkedProp ?? attributes["data-state"];
  const checkboxState = state === "indeterminate" ? "indeterminate" : state === true || state === "checked";
  const checked = checkboxState === true;
  const disabled = attributes["aria-disabled"] === true || attributes["aria-disabled"] === "true";

  const rootProps = {
    "data-checked": checked || undefined,
    // Under cmdk, which spends `aria-selected` on the highlighted row, this is how a picked option is announced.
    "aria-checked": checkedProp === "indeterminate" ? "mixed" : checkedProp,
    // The chevron sits closer to the row's edge.
    className: cn(menuItemVariants({ color, variant }), hasSubmenu && "pr-2", className),
    ...props,
  } as const;

  const content = (
    <>
      {/* The row is what's clicked and announced, so the checkbox is only its picture. */}
      {variant === "checkbox" && (
        <Checkbox.Root
          size="sm"
          checked={checkboxState}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden
          className="pointer-events-none"
        />
      )}
      {variant === "radio" && (
        <RadioGroup.Mark
          checked={checked}
          disabled={disabled}
          // A highlighted row highlights its unchecked radio, as hovering the radio itself would.
          className="group-data-highlighted:data-[state=unchecked]:border-primary-50 group-data-highlighted:data-[state=unchecked]:bg-primary-95 group-data-[selected=true]:data-[state=unchecked]:border-primary-50 group-data-[selected=true]:data-[state=unchecked]:bg-primary-95"
        />
      )}
      <MenuItemLeading leading={leading} color={color} />
      {children}
      {label != null && (
        // Wraps instead of truncating, so an option always reads in full; the description drops below the label when
        // the two don't fit side by side. It takes the row's room, so what follows sits at the end.
        <span className="flex min-w-0 flex-1 flex-wrap gap-x-2">
          <span className="min-w-0 break-words">{label}</span>
          {description != null && (
            <span className="min-w-0 break-words text-neutral-50 group-aria-disabled:text-neutral-80 group-aria-disabled:group-data-checked:text-neutral-70">
              {description}
            </span>
          )}
        </span>
      )}
      {/* `false` too, from a `condition && <…>`: an empty slot would still take a gap off the label. */}
      {trailing != null && trailing !== false && (
        <span className="ml-auto flex shrink-0 items-center gap-3">{trailing}</span>
      )}
      {variant === "default" && checked && (
        <CheckIcon
          data-slot="check"
          className="ml-auto size-5 shrink-0 text-primary-50 group-aria-disabled:text-neutral-70"
        />
      )}
      {shortcut != null && <ItemShortcut shortcut={shortcut} />}
      {hasSubmenu && <ChevronRightIcon className={cn(menuItemIconVariants({ color }), "ml-auto size-4")} />}
    </>
  );

  // The primitive driving the row hands it a `ref` and its handlers, which an anchor takes as well as a div.
  return link ? (
    <Link {...link} {...(rootProps as ComponentProps<"a">)} ref={ref as Ref<HTMLAnchorElement>}>
      {content}
    </Link>
  ) : (
    <div ref={ref} {...rootProps}>
      {content}
    </div>
  );
};

MenuItem.displayName = "MenuItem";
